import { Field, useFormikContext } from 'formik';
import moment from 'moment/moment';
import { useEffect, useState, useContext, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import DefaultLink from 'components/common/DefaultLink';
import Text from 'components/common/Text';
import PrivacyPolicy from 'components/shared/PrivacyPolicy';
import TermsOfService from 'components/shared/TermsOfService';
import { BrandContext, HOME_CHEF } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import { getServingsForDisplay } from 'helpers/servings';
import useBrand from 'hooks/useBrand';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import { PRIVACY_POLICY_PATH } from 'lib/constants';
import { Statuses } from 'lib/constants/signUp';

const Terms = ({ box, user, values }) => {
  const { errors, touched } = useFormikContext();

  const [autoRenewalProtectedState, setAutoRenewalProtectedState] = useState(false);
  const autoRenewalProtectedStates = ['CA', 'NY', 'OR'];
  const { brand } = useContext(BrandContext);
  const isHomeChef = useBrand(HOME_CHEF);
  const mealPlanSlug = useFunnelMealPlan();
  const brandName = brandData(brand)?.displayName;
  const homeChefDisplayName = brandData(HOME_CHEF)?.displayName;
  const transitionRef = useRef();
  const displayServings = getServingsForDisplay(user?.servings, mealPlanSlug);

  useEffect(() => {
    autoRenewalProtectedStates.includes(values?.state)
      ? setAutoRenewalProtectedState(true)
      : setAutoRenewalProtectedState(false);
  }, [values]);

  // Common text components
  const BrandDisclaimer = () => (
    <>
      {brand !== HOME_CHEF && (
        <Text htmlElement="span" brandStylesOverrides="text-base leading-5">
          {brandName} is a {homeChefDisplayName} brand.{' '}
        </Text>
      )}
    </>
  );

  const HomeChefDisplay = ({ otherText }) => (
    <Text htmlElement="span" brandStylesOverrides="text-base leading-5">
      {isHomeChef ? otherText : `${homeChefDisplayName}'s`}
    </Text>
  );

  const TermsOfServiceLink = ({ classNames = 'text-base leading-5 underline px-1' }) => (
    <TermsOfService
      classNames={classNames}
      text="Terms and Conditions of Service"
      location={Statuses.BILLING_STEP}
    />
  );

  TermsOfServiceLink.propTypes = {
    classNames: PropTypes.string,
  };

  const PrivacyPolicyLink = ({ classNames = 'p-0 text-base leading-5 underline' }) => (
    <DefaultLink isExternal to={PRIVACY_POLICY_PATH} className={classNames} target="_blank">
      Privacy Policy
    </DefaultLink>
  );

  PrivacyPolicyLink.propTypes = {
    classNames: PropTypes.string,
  };

  const AgeRequirement = () => (
    <Text htmlElement="span" brandStylesOverrides="text-base leading-5">
      You must be 18 or over to create an account.
    </Text>
  );

  // Common pricing text
  const PricingText = ({ isSpecialTerms = false }) => {
    const baseText = isSpecialTerms
      ? `billed ${box?.afterSignUpTotalFormatted} weekly for ${user.weeklyMeals} meals`
      : `my payment method will be charged ${box?.afterSignUpTotalFormatted}`;

    return (
      <>
        {baseText}
        {!isSpecialTerms && ' (plus applicable sales tax) weekly'}
        {isSpecialTerms && isHomeChef && (
          <Text htmlElement="span" brandStylesOverrides="text-base leading-5">
            {' '}
            at {displayServings} servings
          </Text>
        )}{' '}
        unless
        {isSpecialTerms
          ? ' you make modifications to your order or'
          : ' I modify my order, pause or cancel my account, or'}{' '}
        <Text htmlElement="span" brandStylesOverrides="text-base leading-5">
          {brandName}
        </Text>{' '}
        notifies {isSpecialTerms ? 'you' : 'me'} of a price adjustment.
      </>
    );
  };

  PricingText.propTypes = {
    isSpecialTerms: PropTypes.bool,
  };

  // Regular terms and conditions
  const RegularTermsAndConditions = () => (
    <Text className="-mt-1 pl-5 text-base leading-5" htmlElement="span">
      <BrandDisclaimer />
      By continuing, I accept <HomeChefDisplay otherText="the" />
      <TermsOfServiceLink />
      and <PrivacyPolicyLink />
      . I acknowledge that my subscription automatically renews, and that following the conclusion
      or termination of any promotional discount or offer <PricingText /> <AgeRequirement />
    </Text>
  );

  // Special terms for auto-renewal protected states
  const SpecialTermsAndConditions = () => (
    <Text className="pb-4 text-base leading-5">
      By clicking "Sign Up and Pick Meals," you agree that{' '}
      <Text htmlElement="span" className="text-base font-bold leading-5">
        you are purchasing an automatically renewing subscription.
      </Text>{' '}
      You will receive deliveries and be billed to your designated payment method weekly unless you
      1) skip a delivery through our website or mobile application or 2) pause or cancel your
      subscription by selecting the "Pause Your Account" link or the "Cancel Your Account" link on
      the Account Settings page of our site on or before 12 pm CST the{' '}
      {moment(box?.editingCutoff).format('dddd')} before your delivery date. You also agree that
      following the conclusion or termination of any promotional discount or offer you will be{' '}
      <PricingText isSpecialTerms /> <BrandDisclaimer />
      For more information see <HomeChefDisplay otherText="our" />
      <TermsOfServiceLink classNames="underline text-base p-0 px-1" />
      and
      <DefaultLink
        to={brandData(brand).supportUrl}
        isExternal
        className="p-0 pl-1 text-base underline"
        target="_blank"
      >
        FAQs
      </DefaultLink>
      . <AgeRequirement />
    </Text>
  );

  // Special checkbox text for auto-renewal protected states
  const SpecialStatesCheckBox = () => (
    <Text className="-mt-1 pl-5 text-base" htmlElement="span">
      <BrandDisclaimer />
      By continuing, I accept <HomeChefDisplay otherText="the" />
      <Text htmlElement="span" className="text-base font-bold leading-5">
        {' '}
        Automatic Renewal Terms above
      </Text>
      , <HomeChefDisplay otherText="the" />
      <TermsOfServiceLink classNames="underline text-base p-0 px-1" />
      and the
      <PrivacyPolicy classNames="underline text-base pl-1" text="Privacy Policy" />
    </Text>
  );

  return (
    <div className="mx-auto mt-6 w-full">
      <section>
        <div role="group" aria-labelledby="checkbox-group">
          {autoRenewalProtectedState && <SpecialTermsAndConditions />}
          <Field id="termsChecked" type="checkbox" name="termsChecked" className="absolute" />
          <label htmlFor="termsChecked" className="flex">
            {autoRenewalProtectedState ? <SpecialStatesCheckBox /> : <RegularTermsAndConditions />}
          </label>
        </div>
        <CSSTransition
          in={Boolean(touched.termsChecked) && Boolean(errors.termsChecked)}
          timeout={200}
          classNames="fadeInOut"
          unmountOnExit
          nodeRef={transitionRef}
        >
          <div ref={transitionRef}>
            <Text brandStylesOverrides="textSm" className="text-red-100">
              {errors.termsChecked}
            </Text>
          </div>
        </CSSTransition>
      </section>
    </div>
  );
};

Terms.propTypes = {
  values: PropTypes.object,
  user: PropTypes.shape({
    weeklyMeals: PropTypes.number.isRequired,
    servings: PropTypes.number.isRequired,
    optIns: PropTypes.arrayOf(PropTypes.string),
  }),
  box: PropTypes.shape({
    subtotalFormatted: PropTypes.string.isRequired,
    subtotalCents: PropTypes.number.isRequired,
    totalFormatted: PropTypes.string.isRequired,
    totalCents: PropTypes.number.isRequired,
    promoApplied: PropTypes.bool.isRequired,
    shippingFormatted: PropTypes.string.isRequired,
    shippingCents: PropTypes.number.isRequired,
    taxFormatted: PropTypes.string.isRequired,
    taxCents: PropTypes.number.isRequired,
    pricePerServingFormatted: PropTypes.string.isRequired,
    pricePerServingCents: PropTypes.number.isRequired,
    afterSignUpTotalFormatted: PropTypes.string.isRequired,
    afterSignUpTotalCents: PropTypes.number.isRequired,
    editingCutoff: PropTypes.string,
    promo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
    }),
  }),
  otherText: PropTypes.string,
};

export default Terms;
